/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Grid, Header, Dropdown, Popup, Checkbox } from 'semantic-ui-react';
import { DateInput } from 'semantic-ui-calendar-react';
import { useCurrentUser, servicesAPI } from '../../_services';
import { history } from '../../_helpers/';
import { copyToClipboard, cloudLogLink, dbUTCDatetimeToLocal2, isV2LanceID } from '../../utils';

const countOptions = [
  { text: '100', value: 100 },
  { text: '250', value: 250 },
  { text: '500', value: 500 },
  { text: '1000', value: 1000 }
];

export const ChargingSessionStates = ({
  phalanxTagId,
  phalanxRef,
  lance,
  isAXON,
  setLanceDetected,
  lastUpdated,
  setLastUpdated
}) => {
  const [statusEntries, setStatusEntries] = useState([]);
  const [filteredStatusEntries, setFilteredStatusEntries] = useState([]);
  const [numStatuses, setNumStatuses] = useState(100);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterDuplicates, setFilterDuplicates] = useState(true);
  const [hideInterfaceCommsEvents, setHideInterfaceCommsEvents] = useState(false);

  const { currentUserCan } = useCurrentUser();

  const displayResistor = resistor => {
    // Resistor value actually describes what the rating is for the attached cable,
    // which for our current tethered lance is known to be 32A, but for Lance V2
    // would be more dynamic
    if (resistor === 0) return 'ERR';
    else if (resistor === 10) return '63A';
    else if (resistor === 22) return '32A';
    else if (resistor === 68) return '20A';
    else if (resistor === 150) return '10A';
    else if (resistor === 255) return 'OPEN';
    else return resistor;
  };

  const splitText = text => {
    if (!text || text.length < 25) return text;

    const parts = text.match(/.{1,25}/g);
    return parts.map((ev, idx) => (
      <>
        {ev}
        {idx != parts.length ? <br /> : ''}
      </>
    ));
  };

  useEffect(() => {
    if (!phalanxRef) return;

    const timerInterval = setInterval(() => {
      if (!phalanxRef) {
        console.log('Skipping polling');
        return;
      }

      servicesAPI
        .AdminServices('getPhalanxLastStatusUpdate', phalanxRef)
        .then(response => {
          // If searching specific date/time range, don't refresh to get most recent states
          if (startDate && endDate) return;

          if (lastUpdated !== response) setLastUpdated(response);
        });
    }, 5000);

    // This useEffect cleanup function prevents the Javascript timer running forever
    return () => {
      clearInterval(timerInterval);
    };
  }, [phalanxRef]);

  const convertDateToAPITimestamp = dt => {
    const bits = dt.split('/');
    return `${bits[2]}-${bits[1]}-${bits[0]}T00:00:00`;
  };

  useEffect(() => {
    console.log('Updating statuses');
    let start = '';
    let end = '';
    let count = numStatuses;

    if (startDate && endDate) {
      start = convertDateToAPITimestamp(startDate);
      end = convertDateToAPITimestamp(endDate);
      count = 0;
    }

    servicesAPI
      .AdminServices('getPhalanxStatusEntries', phalanxTagId, start, end, count)
      .then(statusEntries => {
        const entries = statusEntries ? statusEntries : [];
        setStatusEntries(entries)
      });
  }, [phalanxTagId, numStatuses, startDate, endDate, lastUpdated]); // eslint-disable-line

  useEffect(() => {
    if (!statusEntries.length || isAXON === null || lance === null) return;

    const isV2Lance = isV2LanceID(lance?.reference);

    let state = 'unknown';
    let mainsStatus = 'unknown';
    let inputLineStatus = 'unknown';
    let setChargeProfile = 'unknown';
    let mainsIsOn = false;

    let statuses = [];

    if (filterDuplicates) {
      statusEntries.forEach(s => {
        if (!s.state && !s.event) return;

        if (s.event) {
          s.msg = 'EVENT';
          // Suppress AXON 'SPSFault_' messages as they're always followed by 'SPSFault_Comms'
          // if (s.event.endsWith('_')) return;

          // Suppress AXON SPSFault except in C2 as it is often incorrectly reported in EvInitialState
          if (s.state !== 'EvC2State' && s.event.startsWith('SPSFault')) return;
        }

        if (
          s.state &&
          (s.state.startsWith('MainsO') ||
          s.state.startsWith('Request') ||
          s.state.startsWith('Halls') ||
          s.state.startsWith('RetryLock') ||
          s.state.startsWith('SetChargingProfile') ||
          s.state.endsWith('CommandSent') ||
          s.state.endsWith('CommandAck'))
        ) {
          s.msg = 'EVENT';
          s.event = s.state;
          s.state = '';
        }

        // HACK: Attempt to map lance V2 states in case the CSMS was unable to. This is
        // unavoidable messy since the CSMS may need to map states before it knows the lance id
        if (isV2Lance) {
          if (s.state === 'WaitingPoleRaisedState') s.state = 'WaitingCableConnectedState';
          else if (s.state === 'WaitingPoleDroppedState') s.state = 'WaitingButtonPressedState';
        }

        if (s.msg === 'EVENT') {
          // For recurring states, remove duplicates
          if (s.event.startsWith('MainsO')) {
            if (mainsStatus === 'unknown') {
              if (s.event === 'MainsOn') {
                mainsIsOn = true;
                mainsStatus = s.event;
                statuses.push(s);
              } else if (s.event === 'MainsOff') {
                mainsIsOn = false;
                mainsStatus = s.event;
                statuses.push(s);
              }
            } else if (s.event !== mainsStatus) {
              mainsStatus = s.event;
              statuses.push(s);
            }
          } else if (s.event.startsWith('RequestInputLine')) {
            if (inputLineStatus === 'unknown') {
              if (s.event === 'RequestInputLine0') {
                inputLineStatus = s.event;
                s.event = 'InputLineOff';
                statuses.push(s);
              } else if (s.event === 'RequestInputLine1') {
                inputLineStatus = s.event;
                s.event = 'InputLineOn';
                statuses.push(s);
              }
            } else if (s.event !== inputLineStatus) {
              inputLineStatus = s.event;

              if (s.event === 'RequestInputLine0')
                s.event = 'InputLineOff';
              else if (s.event === 'RequestInputLine1')
                s.event = 'InputLineOn';

              statuses.push(s);
            }
          } else if (s.event.startsWith('SetChargingProfile')) {
            if (setChargeProfile === 'unknown' || s.event !== setChargeProfile) {
              setChargeProfile = s.event;
              statuses.push(s);
            }
          } else {
            statuses.push(s);
          }
        } else if (s.msg === 'STATE_CHANGE') {
          if (state === 'unknown') {
            // If MainsOn is more recent than MainsNotOnYet, ignore MainsNotOnYet
            if (s.state === 'MainsNotOnYet' && mainsIsOn) return;

            state = s.state;
            statuses.push(s);
          } else if (s.state !== state) {
            state = s.state;
            statuses.push(s);
          } else if (statuses[statuses.length - 1].msg === 'STATE_CHANGE') {
            statuses[statuses.length - 1] = s;
          }
        } else {
          // Cartridge / Lance status
          if (s.state) {
            if (state === 'unknown') {
              state = s.state;
              statuses.push(s);
            } else if (s.state !== state) {
              state = s.state;
              statuses.push(s);
            } else {
              statuses[statuses.length - 1] = s;
            }
          }

          statuses.push(s);
        }
      });

      // Remove duplicates
      let prevState = {
        state: '-',
        event: '-'
      };

      let firstStatus = true;
      let statuses2 = statuses;
      statuses = [];
      statuses2.forEach(s => {
        // If duplicate, use first instance of state
        if (s.state === prevState.state && s.event === prevState.event) {
          // For command acknowledgements, show the most recent as the states are correct
          if (s.state && !s.state.endsWith('CommandAck')) {
            // Always show most recent status
            if (statuses.length !== 1) statuses[statuses.length - 1] = s;
          }
        } else {
          prevState = s;
          statuses.push(s);
        }
      });
    } else {
      statuses = statusEntries;
    }

    if (hideInterfaceCommsEvents)
      statuses = statuses.filter(s => !['InterfaceCommsUp', 'InterfaceCommsDown'].includes(s.event));

    let lanceDetected = null;

    statuses = statuses.map(s => {
      // if (s.state === s.event) s.state = '';

      let state = {
        phalanx_tag_id: s.phalanx_tag_id,
        datetime: s.datetime,
        msg: s.msg.replace('_', ' '),
        state: s.state,
        event: '',
        session_id: '',
        charge_profile: '',
        lance_detected: '',
        top: '',
        bottom: '',
        closed: '',
        open: '',
        flood: '',
        ev_connected: '',
        ev_ready: '',
        ev_error: '',
        diode_detected: '',
        diode_valid: '',
        lance_uplock: '',
        lance_solenoid: 0,
        lance_tamper: 0,
        lance_tamper_mag: 0,
        fault: '',
        resistor: '',
        voltage: '',
        mains: '',
        input_line: ''
      };

      if (
        s.msg === 'STATE_CHANGE' ||
        s.msg === 'CARTRIDGE_STATUS' ||
        s.msg === 'LANCE_STATUS'
      ) {
        if (lanceDetected === null) {
          lanceDetected = s.lance_detected;
          setLanceDetected(lanceDetected);
        }

        return {
          phalanx_tag_id: state.phalanx_tag_id,
          datetime: state.datetime,
          msg: state.msg,
          state: s.state,
          event: s.event,
          session_id: s.session_id,
          charge_profile: s.charge_profile,
          lance_detected: s.lance_detected ? 'Y' : 'N',
          top: s.top ? 'Y' : 'N',
          bottom: s.bottom ? 'Y' : 'N',
          closed: s.closed ? 'Y' : 'N',
          open: s.open ? 'Y' : 'N',
          flood: s.flood ? (<b style={{ color: 'red' }}>Y</b>) : 'N',
          ev_connected: s.lance_detected ? s.ev_connected ? 'Y' : 'N' : '-',
          ev_ready: s.lance_detected ? s.ev_ready ? 'Y' : 'N' : '-',
          ev_error: s.lance_detected ? s.ev_error ? (<b style={{ color: 'red' }}>Y</b>) : 'N' : '-',
          diode_detected: s.lance_detected ? s.diode_detected ? 'Y' : 'N' : '-',
          diode_valid: s.lance_detected ? s.diode_valid ? 'Y' : 'N' : '-',
          lance_uplock: s.lance_detected ? s.lance_uplock ? 'Y' : 'N' : '-',
          lance_solenoid: s.lance_detected ? s.lance_solenoid : '',
          lance_tamper: s.lance_detected ? s.lance_tamper : '',
          lance_tamper_mag: s.lance_detected ? s.lance_tamper_mag : '',
          fault: s.lance_detected ? s.fault ? (<b style={{ color: 'red' }}>Y</b>) : 'N' : '-',
          resistor: displayResistor(s.resistor),
          voltage: s.voltage,
          mains: !isAXON ? s.mains : '-',
          input_line: !isAXON ? s.input_line : '-'
        };
      } else if (s.msg === 'EVENT') {
        return {
          phalanx_tag_id: state.phalanx_tag_id,
          datetime: state.datetime,
          msg: state.msg,
          state: s.state,
          event: s.event,
          session_id: s.session_id,
          charge_profile: s.charge_profile,
          lance_detected: s.lance_detected ? 'Y' : 'N',
          top: s.top ? 'Y' : 'N',
          bottom: s.bottom ? 'Y' : 'N',
          closed: s.closed ? 'Y' : 'N',
          open: s.open ? 'Y' : 'N',
          flood: s.flood ? (<b style={{ color: 'red' }}>Y</b>) : 'N',
          ev_connected: s.lance_detected ? s.ev_connected ? 'Y' : 'N' : '-',
          ev_ready: s.lance_detected ? s.ev_ready ? 'Y' : 'N' : '-',
          ev_error: s.lance_detected ? s.ev_error ? (<b style={{ color: 'red' }}>Y</b>) : 'N' : '-',
          diode_detected: s.lance_detected ? s.diode_detected ? 'Y' : 'N' : '-',
          diode_valid: s.lance_detected ? s.diode_valid ? 'Y' : 'N' : '-',
          lance_uplock: s.lance_detected ? s.lance_uplock ? 'Y' : 'N' : '-',
          lance_solenoid: s.lance_detected ? s.lance_solenoid : '',
          lance_tamper: s.lance_detected ? s.lance_tamper : '',
          lance_tamper_mag: s.lance_detected ? s.lance_tamper_mag : '',
          fault: s.lance_detected ? s.fault ? (<b style={{ color: 'red' }}>Y</b>) : 'N' : '-',
          resistor: displayResistor(s.resistor),
          voltage: s.voltage,
          mains: !isAXON ? s.mains : '-',
          input_line: !isAXON ? s.input_line : '-'
        };
      }

      return s;
    });

    setFilteredStatusEntries(statuses);

  }, [statusEntries, isAXON, filterDuplicates, hideInterfaceCommsEvents, lance]); // eslint-disable-line

  return (
    <>
      <Grid>
        <Grid.Column width={3}>
          <Grid.Row>
            <Header as="h4">No. charging station states</Header>
            <Dropdown
              selection
              options={countOptions}
              onChange={(_, { value }) => {
                setNumStatuses(value);
              }}
              value={numStatuses}
            />{' '}
            <Popup
              content="Copy states to clipboard"
              trigger={
                <svg
                  data-icon-name="contentCopyIcon"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  aria-hidden="true"
                  sandboxuid="0"
                  onClick={() => {
                    let txt = '';
                    filteredStatusEntries.map(s => {
                      txt += s.datetime
                        ? dbUTCDatetimeToLocal2(s.datetime)
                        : '';
                      // const msg = s.msg !== null ? s.msg : '';
                      const state = s.state !== null ? s.state : '';
                      const evt = s.event !== null ? s.event : '';
                      // txt += ' ' + msg + ' ' + state + ' ' + evt + '\n';
                      txt += ' ' + state + ' ' + evt + '\n';
                    });
                    copyToClipboard(txt);
                  }}
                >
                  <path fill="none" d="M0 0h24v24H0z" sandboxuid="0"></path><path d="M16 20H5V6H3v14c0 1.1.9 2 2 2h11v-2zm4-4V4c0-1.1-.9-2-2-2H9c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h9c1.1 0 2-.9 2-2zm-2 0H9V4h9v12z" sandboxuid="0"></path>
                </svg>
              }
            />
            {currentUserCan('BetaTest') ? (
              <>
                <Header as="h4">Start/end dates</Header>
                <span>
                  <DateInput
                    name="date"
                    placeholder="Start date"
                    value={startDate}
                    dateFormat="DD/MM/YYYY"
                    onChange={(e, inputProps) => {
                      setStartDate(inputProps.value);
                      setEndDate(inputProps.value);
                    }}
                    animation={false}
                    style={{ width: 150 }}
                  />
                  -
                  <DateInput
                    name="date"
                    placeholder="End date"
                    value={endDate}
                    dateFormat="DD/MM/YYYY"
                    onChange={(e, inputProps) => {
                      setEndDate(inputProps.value);
                    }}
                    animation={false}
                    style={{ width: 150 }}
                  />
                </span>
              </>
            ) : ''}
            <br />
            <br />
            <Checkbox
              toggle
              label={'Filter duplicate states'}
              checked={filterDuplicates}
              onChange={e => setFilterDuplicates(!filterDuplicates)}
            />
            <br />
            <br />
            <Checkbox
              toggle
              label={'Hide interface comms events'}
              checked={hideInterfaceCommsEvents}
              onChange={e => setHideInterfaceCommsEvents(!hideInterfaceCommsEvents)}
            />
          </Grid.Row>
        </Grid.Column>
      </Grid>
      {filteredStatusEntries.length > 0 && (
        <>
          <Grid>
            <div role="region" aria-labelledby="caption" tabindex="0">
              <table className="scrollable">
                <thead>
                  <tr />
                  <tr>
                    <th style={{ background: 'white' }}>
                      <div>
                        <span>Time</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }}>
                      <div>
                        <span>State</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }}>
                      <div>
                        <span>Event</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Charge</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Lance Detected</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Top</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Bottom</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Closed</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Open</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Flood</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>EV Connected</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>EV Ready</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>EV Error</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Diode Detect</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Diode Valid</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Lance Uplock</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Lance Solenoid</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Lance Tamper</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Lance Tamper Mag</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Fault</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Resistor</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Volt</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Mains</span>
                      </div>
                    </th>
                    <th style={{ background: 'white' }} className="vertical">
                      <div>
                        <span>Input</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStatusEntries.map((s, s_idx) => (
                    <tr key={s_idx}>
                      <td>
                        {s.datetime !== '' ? (
                          <>
                            {dbUTCDatetimeToLocal2(s.datetime).split(' @ ')[0]}
                            <br />{' @ '}
                            <span>
                              {dbUTCDatetimeToLocal2(s.datetime).split(' @ ')[1]}
                              {currentUserCan('ManageGoogleCloud') && (
                                <a
                                  href={cloudLogLink(s.datetime, phalanxRef)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img
                                    width={16}
                                    height={16}
                                    src="/images/gcp_logo.png"
                                    alt="Google Cloud logo"
                                  />
                                </a>
                              )}
                            </span>
                          </>
                        ) : ''}
                      </td>
                      <td>{s.state}</td>
                      <td>{splitText(s.event)}</td>
                      <td>{s.charge_profile}</td>
                      <td>{s.lance_detected}</td>
                      <td>{s.top}</td>
                      <td>{s.bottom}</td>
                      <td>{s.closed}</td>
                      <td>{s.open}</td>
                      <td>{s.flood}</td>
                      <td>{s.ev_connected}</td>
                      <td>{s.ev_ready}</td>
                      <td>{s.ev_error}</td>
                      <td>{s.diode_detected}</td>
                      <td>{s.diode_valid}</td>
                      <td>{s.lance_uplock}</td>
                      <td>{s.lance_solenoid}</td>
                      <td>{s.lance_tamper}</td>
                      <td>{s.lance_tamper_mag}</td>
                      <td>{s.fault}</td>
                      <td>{s.resistor}</td>
                      <td>{s.voltage}</td>
                      <td>{s.mains}</td>
                      <td>{s.input_line}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Grid>
          <br />
          <p>Last updated: {dbUTCDatetimeToLocal2(lastUpdated)}</p>
        </>
      )}
    </>
  );
};
